/**
 * @fileoverview The row of buttons under a picture being edited: Rotate left, Rotate
 * right, Crop (with Square and Free while there is a box), and, once the
 * photo has been edited before, a quiet "Reset to original".
 *
 * The two turns are icons, named for a screen reader and on hover, so the
 * row fits a phone in Spanish too; Crop says its name, as it is the one that
 * changes what the picture shows.
 *
 * Where: under the picture in EditPhotoDialog and ImageEditDialog.
 * API: none; Reset is the caller's.
 * Settings: none.
 * Words (en/es): plants.photo.edit.* (rotateLeft, rotateRight, crop,
 * square, free, reset, resetting).
 *
 * @module ImageEditTools
 */

import React from 'react';
import { Crop, RotateCcw, RotateCw } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { CropShape } from '@/shared/lib/imageEdit';
import type { ImageEditState } from './useImageEdit';

/** The look of each small button in the row. */
const TOOL = 'inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md border bg-background text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/** The edit the buttons change, and the reset beside them. */
interface ImageEditToolsProps {
  /** The edit being made (useImageEdit). */
  state: ImageEditState;
  /** While saving. */
  disabled?: boolean;
  /** When the stored picture is already an edit, how to put the original back. */
  onReset?: () => void;
  /** True while the original is being put back. */
  resetting?: boolean;
}

/**
 * Rotate left, Rotate right, Crop, Square or Free, and Reset to original.
 *
 * @param props - ImageEditToolsProps.
 */
export const ImageEditTools: React.FC<ImageEditToolsProps> = ({ state, disabled, onReset, resetting }) => {
  const { reactTranslate: t } = useTranslation();
  const cropping = state.edit.crop !== null;

  /**
   * One of the two shape buttons.
   *
   * @param shape - Square or Free.
   * @param label - Its name.
   */
  const shapeButton = (shape: CropShape, label: string): React.ReactNode => (
    <button
      type="button"
      aria-pressed={state.shape === shape}
      onClick={() => state.setShape(shape)}
      disabled={disabled}
      className={cn(
        'h-8 rounded px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        state.shape === shape ? 'bg-background font-semibold shadow-sm' : 'text-muted-foreground',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={() => state.turn('left')}
        disabled={disabled}
        aria-label={t('plants.photo.edit.rotateLeft')}
        title={t('plants.photo.edit.rotateLeft')}
        className={cn(TOOL, 'w-10')}
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => state.turn('right')}
        disabled={disabled}
        aria-label={t('plants.photo.edit.rotateRight')}
        title={t('plants.photo.edit.rotateRight')}
        className={cn(TOOL, 'w-10')}
      >
        <RotateCw className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={state.toggleCrop}
        disabled={disabled}
        aria-pressed={cropping}
        className={cn(TOOL, 'px-3', cropping && 'border-primary bg-primary/10 font-semibold')}
      >
        <Crop className="h-4 w-4" aria-hidden="true" />
        {t('plants.photo.edit.crop')}
      </button>
      {cropping && (
        <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted p-1">
          {shapeButton('square', t('plants.photo.edit.square'))}
          {shapeButton('free', t('plants.photo.edit.free'))}
        </div>
      )}
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          disabled={disabled || resetting}
          className="ml-auto min-h-10 shrink-0 px-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
        >
          {resetting ? t('plants.photo.edit.resetting') : t('plants.photo.edit.reset')}
        </button>
      )}
    </div>
  );
};
